/**
 * 통합검색 미디어 화면의 값 — 탭 · 기간 · 채널 · 형식 · 정렬 · 한 번에 불러오는 수.
 *
 * 미디어는 **광고 소재와 오가닉 게시물**을 한 그리드에 편다. 판매몰 상품은 「상품」 탭에서만 따로 편다(섞지 않는다 — 그림·정렬 기준이 다르다).
 * ⚠️ 정렬은 갈래가 있다 — 공통(최신순)은 두 원장을 섞고, 「광고만」「오가닉만」 정렬은 그 원장 하나만 편다.
 */

import type { MediaFilter } from '../scope/search-all-media-scope';

export {
  MEDIA_TABS,
  MEDIA_FORMATS,
  MEDIA_COUNTRIES,
  MEDIA_AD_CHANNELS,
  MEDIA_LITE_CHANNELS,
  MEDIA_ORGANIC_CHANNELS,
  MEDIA_ORGANIC_CHANNEL_GROUPS,
} from 'src/constants/media-filters';
export type { MediaTab, MediaFormat, MediaChannel, MediaCountry } from 'src/constants/media-filters';

/** 기간 — 오늘까지 거슬러 올라가는 일수. 화면에 고르개가 없고 늘 이 값이다(라이트 플랜 상한과 같다 · 어느 플랜도 이보다 짧지 않다) */
export const MEDIA_DEFAULT_DAYS = 30;

/** 정렬 — 공통(최신순 · 인기순: 광고는 오래 집행된 순, 오가닉은 성과 점수 순) · 광고만 · 오가닉만 */
export const MEDIA_SORTS = ['recent', 'popular', 'oldest', 'longest', 'views', 'likes', 'comments'] as const;
export type MediaSort = (typeof MEDIA_SORTS)[number];

/**
 * 이 화면이 늘 쓰는 정렬 — **화면에 고르개가 없다**(네 탭 모두 못 박혀 있다).
 * 🔑 값은 그대로 서버에 실린다 — 고르개만 없앴지 정렬을 끈 것이 아니다.
 */
export const MEDIA_DEFAULT_SORT: MediaSort = 'popular';

/**
 * 조건이 하나도 걸리지 않은 필터 — 검색어 빈 값 · 전체 탭 · 기본 기간 · 좁히기 없음 · 기본 정렬.
 * 화면 상태의 첫 값과 공유 페이지 배경 툴바가 쓴다. ⚠️ `query` 가 첫 칸이다 — 덮어쓸 때 `{ ...MEDIA_DEFAULT_FILTER, query }`.
 */
export const MEDIA_DEFAULT_FILTER: MediaFilter = {
  query: '',
  tab: 'all',
  days: MEDIA_DEFAULT_DAYS,
  channels: [],
  formats: [],
  countries: [],
  languages: [],
  sort: MEDIA_DEFAULT_SORT,
};

/**
 * 상품 탭 정렬 — `relevance` 는 관련도순(서버에 정렬을 싣지 않는다). ⚠️ 검색은 날짜 무관이라 순위순이 없다.
 * 🔑 화면은 늘 `relevance` 다 — 고르개가 없다. 나머지 값은 갈래를 적어 둔 것이다.
 */
export const PRODUCT_SORTS = ['relevance', 'discount', 'priceAsc', 'priceDesc', 'reviews', 'rating'] as const;
export type ProductSort = (typeof PRODUCT_SORTS)[number];

/** 한 번에 불러오는 카드 수 — 둘 다 펼 때는 원장마다 반씩 */
export const MEDIA_PAGE_SIZE = 24;

/**
 * 목록을 펴는 네 가지 **보기** — 같은 카드 목록을 다르게 편다.
 * ```
 *   thumb  실물만 촘촘히 (무엇이 있나 훑을 때)
 *   card   실물 + 머리·발  (기본 · 결을 볼 때)
 *   list   한 줄에 하나  (본문까지 읽을 때)
 *   table  칸으로 나눈 줄 (수치를 견줄 때)
 * ```
 */
export const MEDIA_VIEWS = ['thumb', 'card', 'list', 'table'] as const;
export type MediaView = (typeof MEDIA_VIEWS)[number];
/** 처음 여는 보기 — 훑는 자리라 실물이 꽉 찬 섬네일부터 준다 */
export const MEDIA_DEFAULT_VIEW: MediaView = 'thumb';

/**
 * 전환 카드를 **몇 번째 카드 앞**에 끼울지 — 첫 줄을 다 보여 준 다음이다.
 * ⚠️ 줄당 장 수는 화면 폭이 정하므로(정렬 배치) 「둘째 줄 첫 칸」이 못 박히지는 않는다.
 *   맨 앞(0)만 피하면 된다 — 찾아온 소재보다 유도가 먼저 보이면 안 된다.
 */
export const CONVERSION_CARD_AT = 4;
/**
 * 전환 카드를 **몇 장마다** 다시 세울지 — 이어 불러오며 한참 내려가도 계속 만난다.
 * ⚠️ 한 화면에 둘이 보일 만큼 좁히지 마라 — 같은 질문이 나란히 서면 광고로 읽힌다.
 */
export const CONVERSION_CARD_EVERY = 20;
/** 전환 카드의 칸 비율(가로/세로) — 세로 카드와 같은 모양이라 줄이 흐트러지지 않는다 */
export const CONVERSION_CARD_RATIO = 0.8;

/**
 * 찾은 말이 든 카드에 두르는 **테** — 섬네일·카드·리스트 보기가 같은 테를 쓴다(표는 제 줄 바탕).
 * 🔑 `outline` 이라 칸 크기가 안 변한다 — `border` 를 굵히면 정렬 배치의 줄 폭이 어긋난다.
 * ⚠️ 바깥으로 3px 나간다 — 칸 사이 여백(`GAP` 8px) 안에 들어간다. 테의 모서리는 칸의 `borderRadius` 를 따른다(붙이는 쪽이 준다).
 */
export const MEDIA_EMPHASIS_SX = {
  outline: '2px solid',
  outlineColor: 'primary.main',
  outlineOffset: '1px',
} as const;

/**
 * 리스트 보기의 테 — 맨 줄이라 글이 테에 붙고 구분선과 겹치므로, 테를 **안쪽**에 그리고 좌우로 한 뼘 넓혀 글과 띄운다.
 * ⚠️ 좌우로 12px 나간다 — 목록을 감싼 칸의 안쪽 여백 안에 들어간다.
 */
export const MEDIA_LIST_EMPHASIS_SX = {
  ...MEDIA_EMPHASIS_SX,
  outlineOffset: '-2px',
  mx: -1.5,
  px: 1.5,
} as const;
